/**
 * Mirror renderer `console.log` / `.warn` / `.error` (and uncaught errors)
 * into:
 *   - The Electron main process so they land in the same daily log file as
 *     the main-process logs (file = offline-resilient copy).
 *   - The cloud log shipper (Supabase via /api/logs/batch) so the owner can
 *     review counter activity remotely (cloud = durable, remotely queryable).
 *
 * No-op outside Electron for the file sink. Cloud sink works on web too.
 */

import { queueCloudLog, installCloudLogger } from '../services/cloudLogger';

let installed = false;

const serialize = (args) => {
  try {
    return args
      .map((a) => {
        if (a instanceof Error) {
          return `${a.name}: ${a.message}${a.stack ? `\n${a.stack}` : ''}`;
        }
        if (typeof a === 'string') return a;
        try {
          return JSON.stringify(a, (_k, v) => (v instanceof Error ? `${v.name}: ${v.message}` : v));
        } catch (_) {
          return String(a);
        }
      })
      .join(' ');
  } catch (_) {
    return '<unserializable>';
  }
};

export function installFileLogger() {
  if (installed) return;
  installed = true;

  // Always set up cloud shipper (works on web + Electron).
  try { installCloudLogger(); } catch (_) { /* never break boot */ }

  const electronApi = typeof window !== 'undefined' ? window.electronAPI : null;
  const fileSink    = electronApi?.logToFile ? electronApi : null;

  const wrap = (level, original) =>
    function (...args) {
      const text = serialize(args);
      if (fileSink) {
        try { fileSink.logToFile(level, text); } catch (_) { /* never break the call */ }
      }
      try { queueCloudLog({ level, message: text }); } catch (_) { /* ignore */ }
      try { original.apply(console, args); } catch (_) { /* ignore */ }
    };

  // Wrap the four channels we care about. info shares the 'info' file level.
  // eslint-disable-next-line no-console
  console.log   = wrap('info',  console.log);
  // eslint-disable-next-line no-console
  console.info  = wrap('info',  console.info);
  // eslint-disable-next-line no-console
  console.warn  = wrap('warn',  console.warn);
  // eslint-disable-next-line no-console
  console.error = wrap('error', console.error);

  // Surface uncaught errors & unhandled rejections so they reach BOTH sinks
  // even if no component is around to log them.
  window.addEventListener('error', (e) => {
    const msg = `[uncaught] ${e?.message || ''} ${e?.error?.stack || ''}`.trim();
    if (fileSink) { try { fileSink.logToFile('error', msg); } catch (_) {} }
    try { queueCloudLog({ level: 'error', message: msg, tag: 'uncaught' }); } catch (_) {}
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    const text = reason instanceof Error
      ? `${reason.name}: ${reason.message}\n${reason.stack || ''}`
      : serialize([reason]);
    const msg = `[unhandledrejection] ${text}`;
    if (fileSink) { try { fileSink.logToFile('error', msg); } catch (_) {} }
    try { queueCloudLog({ level: 'error', message: msg, tag: 'unhandledrejection' }); } catch (_) {}
  });
}
